import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { Select, Input, Button, Checkbox, Progress, Modal, Spin } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import PageHeader from 'components/pageHeader';
import Notification from 'services/Notification';
import { startAiTestHandoff, setAiHandoff } from 'actions/testAdmin';
import dropdownArrow from '../../../Assets/schedule/dropdownArrow.svg';
import ScanReview from './ScanReview';
import {
  chapterAvailable,
  loadInstituteChapters,
  loadInstituteSubjects,
  matchingSubjects
} from './scanPdfInstitute';
import {
  DROPDOWN_BASE,
  MAX_PDFS,
  MAX_PDF_MB,
  POLL_INTERVAL_MS,
  checkParser,
  getJob,
  getQuestions,
  parserConfigured,
  patchQuestion,
  popupInside,
  toChapterOptions,
  toOptions,
  uploadPdf
} from './scanPdfUtils';
import {
  MIXED,
  TYPE_LABELS,
  buildGeneratedTest,
  buildMarking,
  buildMarkingByType,
  buildSelection,
  buildUnits,
  chapterFor,
  chapterId,
  chapterSubject,
  checkQuestion,
  sameSubject,
  composeQuestion,
  emptyAnswer,
  findPairsPerJob,
  groupChaptersByClass,
  isImportable,
  needsLook,
  parsePrintedAnswer,
  patternMarkingRule,
  shareOf,
  questionMarks,
  reviewStatus,
  sanitizeAnswer,
  subjectFor,
  syllabusFor,
  tickedNames,
  withSubjects,
  NEW_PASSAGE,
  applyPassageEdits,
  passageChoices
} from './scanPdfMapper';
import './scan-pdf.css';

// An objective test (single, multiple, numeric, true/false, matching, passages) and a subjective test are different
// kinds of test in this app. Which one a paper makes is worked out, not asked: JEE and NEET papers are objective, and
// any other paper is what its questions are. Only a paper holding both kinds asks the teacher which to keep.

const STORAGE_KEY = 'scanPdfReview.v2';
const LEGACY_STORAGE_KEY = 'scanPdfReview.v1'; // one PDF, one job

// The saved review as { jobs: [{ id, filename, subject }], ... }. A review saved before several PDFs
// were allowed holds a single jobId, so it is carried over as one job.
const readSaved = () => {
  try {
    const current = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    if (current && Array.isArray(current.jobs) && current.jobs.length) return current;
    const legacy = JSON.parse(window.localStorage.getItem(LEGACY_STORAGE_KEY) || 'null');
    if (legacy && legacy.jobId) {
      const subject = legacy.selection && legacy.selection.subject && legacy.selection.subject[0];
      return { ...legacy, jobs: [{ id: legacy.jobId, filename: '', subject: subject || '' }] };
    }
    return null;
  } catch (err) {
    return null;
  }
};
const writeSaved = (value) => {
  try {
    if (value) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch (err) {
    // storage can be blocked; the screen still works without resume
  }
};
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Was the page itself reloaded (a refresh) just before this screen opened? The browser keeps saying "reload" for the
// whole life of the page, so only the first time the screen opens after a page load counts.
let firstOpenSinceLoad = true;

// The chosen PDFs, kept in memory while the teacher moves to Create Test and back (a File cannot be saved in the
// browser's storage). A refresh clears it, as it clears everything else.
let keptFiles = [];
const pageWasReloaded = () => {
  const first = firstOpenSinceLoad;
  firstOpenSinceLoad = false;
  if (!first) return false;
  try {
    const nav = window.performance && window.performance.getEntriesByType && window.performance.getEntriesByType('navigation')[0];
    // Only a refresh of this screen itself starts it empty. A refresh on Create Test, then its back arrow, is a
    // teacher coming back to their review.
    return Boolean(nav && nav.type === 'reload' && new URL(nav.name).pathname === window.location.pathname);
  } catch (err) {
    return false;
  }
};
const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

// A pattern's name, squeezed to letters, for telling the kinds of paper apart: "JEE Main(s)", "JEE Advanced", "NEET".
const patternKey = (p) => String((p && (p.label || p.value)) || p || '').toLowerCase().replace(/[^a-z]/g, '');
// JEE (Main and Advanced) and NEET papers are objective only.
const isObjectiveOnlyPattern = (p) => /jee|neet/.test(patternKey(p));

// What a dropdown says when nothing matches what was typed (antd's own "No data" says nothing useful).
const NOT_FOUND = {
  testkind: 'No question types found',
  patterns: 'No patterns found',
  classes: 'No classes found',
  subjects: 'No subjects found'
};

// A chapter is ticked as its subject + name, so a name two subjects share is still two separate chapters.
const idOf = (chapter) => chapterId(chapter.subjectValue, chapter.value);

const jobStatusText = (phase, data) => {
  if (phase === 'uploading') return 'Uploading...';
  if (!data) return 'Starting...';
  if (data.status === 'completed') return 'Done';
  if (data.status === 'partial') return 'Done, some pages could not be read';
  if (data.status === 'failed') return 'Could not be read';
  if (data.stage === 'answering' && data.answers_total) {
    return `Solving answer ${Math.min((data.answers_done || 0) + 1, data.answers_total)} of ${data.answers_total}`;
  }
  if (data.total_pages) return `Reading page ${Math.min(data.pages_done + 1, data.total_pages)} of ${data.total_pages}`;
  return data.status === 'queued' ? 'Waiting its turn...' : 'Starting...';
};

const ScanPdfTab = ({ startAiTestHandoff, setAiHandoff, handoffLoading }) => {
  const history = useHistory();
  const location = useLocation();
  const [parserState, setParserState] = useState(parserConfigured() ? 'checking' : 'unconfigured');
  const [patterns, setPatterns] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState({});
  const [selection, setSelection] = useState({ pattern: '', class: '', subject: [], chapters: [] });
  // Empty until a pattern is picked: its own marking fills in, or, for a pattern without one, the teacher types it.
  const [marks, setMarks] = useState({ correct: '', incorrect: '' });
  // The PDFs chosen on this visit, each tagged with the subject it holds: [{ key, file, subject }].
  const [files, setFiles] = useState([]);
  const [fileKey, setFileKey] = useState(0); // changing it clears the browser's own file box
  const [generateAnswers, setGenerateAnswers] = useState(false); // solve the questions the paper leaves unanswered
  // After Start over, a PDF is read again from the start even if it was read before (the service would otherwise
  // hand back its earlier result for the same file).
  const [readAfresh, setReadAfresh] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | uploading | extracting | ready
  // The reading jobs on the parser, one per PDF: [{ id, filename, subject, data }].
  const [jobs, setJobs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [keep, setKeep] = useState({});
  const [answers, setAnswers] = useState({});
  // Hindi twin id -> English question id, for questions printed in both languages and joined into one card.
  const [pairs, setPairs] = useState({});
  const [modes, setModes] = useState({}); // unit id -> 'both' | 'en' | 'hi'
  // The scan service checks each question against the subject and chapters the teacher chose. A mismatch is a
  // warning only. `overrides` is the chapter the teacher moved a question to; `reviewed` is a question whose
  // warning the teacher has looked at and decided to keep as chosen.
  const [overrides, setOverrides] = useState({});
  const [reviewed, setReviewed] = useState({});
  // For a question the AI places in another subject of the test: 'ai' or 'mine' once the teacher decides; until
  // then it follows the AI.
  const [subjectChoice, setSubjectChoice] = useState({});
  const [title, setTitle] = useState(''); // optional; the Create Test form builds one when this is left empty
  // 'objective' | 'subjective' | 'hybrid': set from what the paper holds once it is read; empty until then
  const [testKind, setTestKind] = useState('');
  // A hybrid paper's marks for each written question; negative marks are optional.
  const [subjMarks, setSubjMarks] = useState({ correct: '', incorrect: '' });
  // The marks card folded into one line once the teacher pressed Done.
  const [marksDone, setMarksDone] = useState(false);
  // Marks the teacher set on single questions in Edit: id -> { correct, incorrect }.
  const [qMarks, setQMarks] = useState({});
  // The form as it was when Extract last ran. Extract stays off until something changes, so a second press
  // cannot read the same PDFs again for nothing.
  const [extractedSig, setExtractedSig] = useState('');
  const [preview, setPreview] = useState(null); // { name, url } of the PDF being looked at
  const [proceeding, setProceeding] = useState(false);
  const fileInput = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const [focusId, setFocusId] = useState(null);
  const savedReview = useRef(null);
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  // The review in progress is kept when the screen is left, so coming back to it (the back arrow on Create Test, the
  // browser's back button) shows it again. Opening the screen afresh (from the menu or the Test page) starts empty:
  // that is a PUSH, while back is a POP. Create Test's own back arrow is a PUSH too, so it says it is coming back.
  // A refresh starts empty as well: it is a reload of the page, seen only on the first visit after the page loads.
  const reloaded = useRef(pageWasReloaded());
  const filesToRestore = useRef(keptFiles); // read once, as the screen opens, before anything replaces it
  const freshVisit = useRef(
    reloaded.current || (history.action === 'PUSH' && !(location.state && location.state.resumeScan))
  );
  // Every problem is told to the person in the corner notice, as on the other screens.
  const failRun = (message) => {
    Notification.error('Could not read the PDF', message);
    writeSaved(null);
    savedReview.current = null;
    setJobs([]);
    setExtractedSig('');
    setPhase('idle');
  };
  // What the institute itself has set up. 'loading' until read; 'unavailable' means the lists could
  // not be read, and the form then shows every option instead of hiding things by mistake.
  const [institute, setInstitute] = useState({ status: 'loading', subjects: [] });
  const [instChapters, setInstChapters] = useState({}); // generator subject -> { status, keys }

  const kind = testKind || 'objective';
  // The teacher's own passage choices, on top of what was read: which passage a question is under (or none), and
  // the text of passages they wrote themselves.
  const [passageLinks, setPassageLinks] = useState({});
  const [newPassages, setNewPassages] = useState({});
  const units = useMemo(
    () => buildUnits(applyPassageEdits(questions, passageLinks, newPassages), pairs),
    [questions, pairs, passageLinks, newPassages]
  );
  const selectedSubjects = Array.isArray(selection.subject) ? selection.subject : [];
  const busy = phase === 'uploading' || phase === 'extracting';
  const jobIdsKey = jobs.map((j) => j.id).join(',');
  const rule = patternMarkingRule(selection.pattern, selection.class);
  const patternOf = (value) => patterns.find((p) => p.value === value) || value;
  const objectiveOnly = Boolean(selection.pattern) && isObjectiveOnlyPattern(patternOf(selection.pattern));

  // ---- only what the institute really has ----------------------------------------
  const instituteOk = institute.status === 'ok';
  const subjectLabel = useCallback(
    (value) => (subjects.find((s) => s.value === value) || {}).label || value,
    [subjects]
  );
  // Every chapter of the exam pattern is offered. One the institute has not set up yet is marked, and the Create Test
  // screen can add it to the syllabus.
  const visibleChapters = chapters;
  const notInSyllabus = (chapter) => {
    const info = instChapters[chapter.subjectValue];
    return instituteOk && Boolean(info) && info.status === 'ok' && !chapterAvailable(chapter.value, info.keys);
  };
  const missingChapterCount = chapters.filter(notInSyllabus).length;
  const syllabusUnreadable = instituteOk && selectedSubjects.some((v) => instChapters[v] && instChapters[v].status === 'unavailable');
  // Subjects from the standard list that the institute has not set up. They stay selectable, exactly
  // as on the Aulas AI form; the Create Test screen lets the teacher add the subject there.
  const missingSubjects = instituteOk
    ? selectedSubjects.filter((v) => matchingSubjects(subjectLabel(v), institute.subjects).length === 0)
    : [];


  // ---- parser service health ------------------------------------------------
  const runHealthCheck = useCallback(async () => {
    if (!parserConfigured()) return;
    setParserState('checking');
    try {
      await checkParser();
      setParserState('ok');
    } catch (err) {
      setParserState('down');
    }
  }, []);
  useEffect(() => { runHealthCheck(); }, [runHealthCheck]);

  // ---- dropdown chain: identical rules to the Aulas AI form ------------------
  const fetchOptions = useCallback(async (path, field, mapper) => {
    setLoading((prev) => ({ ...prev, [field]: true }));
    try {
      const response = await fetch(`${DROPDOWN_BASE}${path}`);
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
      return mapper(await response.json());
    } catch (err) {
      console.error(`Failed to load ${field}:`, err);
      Notification.error('Could not load', `Could not load ${field}: ${err.message}`);
      return [];
    } finally {
      setLoading((prev) => ({ ...prev, [field]: false }));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const options = await fetchOptions('/api/dropdowns/patterns', 'patterns', toOptions);
      if (!cancelled) setPatterns(options);
    })();
    return () => { cancelled = true; };
  }, [fetchOptions]);

  useEffect(() => {
    if (!selection.pattern) {
      setClasses([]);
      setSubjects([]);
      return undefined;
    }
    let cancelled = false;
    const pattern = encodeURIComponent(selection.pattern);
    (async () => {
      const [classOptions, subjectOptions] = await Promise.all([
        fetchOptions(`/api/dropdowns/classes?pattern=${pattern}`, 'classes', toOptions),
        fetchOptions(`/api/dropdowns/subjects?pattern=${pattern}`, 'subjects', toOptions)
      ]);
      if (cancelled) return;
      setClasses(classOptions);
      setSubjects(subjectOptions);
    })();
    return () => { cancelled = true; };
  }, [selection.pattern, fetchOptions]);

  useEffect(() => {
    if (!selection.pattern || !selection.class || selectedSubjects.length === 0) {
      setChapters([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading((prev) => ({ ...prev, chapters: true }));
      try {
        const results = await Promise.all(
          selectedSubjects.map(async (subjVal) => {
            const query = new URLSearchParams({
              pattern: selection.pattern,
              class: selection.class,
              subject: subjVal
            });
            try {
              const response = await fetch(`${DROPDOWN_BASE}/api/dropdowns/chapters?${query.toString()}`);
              if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
              const options = toChapterOptions(await response.json());
              const subjObj = subjects.find((s) => s.value === subjVal);
              return options.map((item) => ({
                ...item,
                subjectValue: subjVal,
                subjectName: subjObj?.label || subjVal,
                key: `${subjVal}__${item.key || item.value}`
              }));
            } catch (err) {
              console.error(`Failed to load chapters for ${subjVal}:`, err);
              Notification.error('Could not load', `Could not load chapters for ${subjVal}: ${err.message}`);
              return [];
            }
          })
        );
        if (!cancelled) setChapters(results.flat());
      } finally {
        if (!cancelled) setLoading((prev) => ({ ...prev, chapters: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [selection.pattern, selection.class, selection.subject, subjects]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await loadInstituteSubjects();
      if (!cancelled) setInstitute({ status: result.ok ? 'ok' : 'unavailable', subjects: result.subjects });
    })();
    return () => { cancelled = true; };
  }, []);

  // Read the syllabus for each chosen subject once. A subject the institute has under more than
  // one name (or per batch) is merged into one set of chapter names.
  useEffect(() => {
    if (!instituteOk) return;
    selectedSubjects.forEach((value) => {
      if (instChapters[value]) return;
      setInstChapters((prev) => ({ ...prev, [value]: { status: 'loading', keys: new Set() } }));
      const ids = matchingSubjects(subjectLabel(value), institute.subjects).map((x) => x.id);
      if (ids.length === 0) {
        // Nothing to compare against, so every chapter stays on offer (the Create Test screen can add them).
        setInstChapters((prev) => ({ ...prev, [value]: { status: 'missing', keys: new Set() } }));
        return;
      }
      loadInstituteChapters(ids).then((result) => {
        if (!alive.current) return;
        setInstChapters((prev) => ({ ...prev, [value]: { status: result.ok ? 'ok' : 'unavailable', keys: result.keys } }));
      });
    });
  }, [instituteOk, selection.subject, subjects]);

  // Drop a chapter already chosen (or restored from an earlier visit) that the exam pattern no longer lists.
  useEffect(() => {
    if (chapters.length === 0) return;
    const offered = new Set(chapters.map(idOf));
    if (selection.chapters.some((c) => !offered.has(c))) {
      setSelection((prev) => ({ ...prev, chapters: prev.chapters.filter((c) => offered.has(c)) }));
    }
  }, [chapters]);

  const handlePatternChange = (value) => {
    setSelection({ pattern: value, class: '', subject: [], chapters: [] });
    setMarks({ correct: '', incorrect: '' }); // the new pattern brings its own marking, or starts empty
    // Leaving a JEE / NEET pattern for one with a choice: the type it set is no longer the teacher's choice.
    if (objectiveOnly && !isObjectiveOnlyPattern(patternOf(value))) setTestKind('');
  };
  // JEE and NEET are objective only: picking one (or coming back to a review made with one) sets the type.
  useEffect(() => {
    if (objectiveOnly && testKind !== 'objective') handleKindChange('objective');
  }, [objectiveOnly]);
  const handleClassChange = (value) => {
    setSelection((prev) => ({ ...prev, class: value, chapters: [] }));
  };
  // Several subjects make a full mock test. Chapters already ticked stay ticked when they belong to a
  // subject that is still selected; the chapter list itself is re-read for the new set of subjects.
  const handleSubjectChange = (value) => {
    const next = Array.isArray(value) ? value : value ? [value] : [];
    setSelection((prev) => {
      const keepChapters = prev.chapters.filter((id) => next.includes(chapterSubject(id)));
      return { ...prev, subject: next, chapters: keepChapters };
    });
  };
  const setChapterSelection = (next) => setSelection((prev) => ({ ...prev, chapters: next }));
  // Ticking a chapter ticks it for its own subject only, even when another subject has one of the same name.
  const toggleChapter = (chapter) => {
    const id = idOf(chapter);
    setChapterSelection(
      selection.chapters.includes(id) ? selection.chapters.filter((c) => c !== id) : [...selection.chapters, id]
    );
  };
  // Select all / Clear for one subject's chapters, or for every chapter shown.
  const selectChapters = (list) => setChapterSelection(Array.from(new Set([...selection.chapters, ...list.map(idOf)])));
  const clearChapters = (list) => {
    const drop = new Set(list.map(idOf));
    setChapterSelection(selection.chapters.filter((id) => !drop.has(id)));
  };

  // ---- the PDFs ---------------------------------------------------------------
  // With several subjects a PDF is taken to hold all of them, until the teacher says it is one subject's paper.
  const defaultSubject = selectedSubjects.length === 1 ? selectedSubjects[0] : selectedSubjects.length > 1 ? MIXED : '';

  // A PDF tagged with a subject that is no longer selected goes back to the default (or to unset).
  useEffect(() => {
    setFiles((prev) => {
      let changed = false;
      const next = prev.map((entry) => {
        if (entry.subject && selectedSubjects.includes(entry.subject)) return entry;
        if (entry.subject === MIXED && selectedSubjects.length > 1) return entry;
        if (entry.subject === defaultSubject) return entry;
        changed = true;
        return { ...entry, subject: defaultSubject };
      });
      return changed ? next : prev;
    });
  }, [selection.subject]);

  const addFiles = (picked) => {
    const next = [...files];
    const problems = [];
    picked.forEach((f) => {
      if (!/\.pdf$/i.test(f.name) && f.type !== 'application/pdf') {
        problems.push(`${f.name} is not a PDF`);
      } else if (f.size > MAX_PDF_MB * 1024 * 1024) {
        problems.push(`${f.name} is larger than ${MAX_PDF_MB} MB`);
      } else if (next.some((x) => x.file.name === f.name && x.file.size === f.size)) {
        problems.push(`${f.name} is already added`);
      } else if (next.length >= MAX_PDFS) {
        problems.push(`Only ${MAX_PDFS} PDFs can be scanned at a time`);
      } else {
        next.push({ key: `${Date.now()}-${next.length}-${f.name}`, file: f, subject: defaultSubject });
      }
    });
    setFiles(next);
    setFileKey((k) => k + 1);
    if (problems.length) Notification.error('PDF not added', problems.join('. '));
  };
  // The PDF is shown from the copy already on this computer, so nothing is sent anywhere to look at it.
  const openPreview = (entry) => setPreview({ name: entry.file.name, url: URL.createObjectURL(entry.file) });
  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };
  const setFileSubject = (key, subject) =>
    setFiles((prev) => prev.map((entry) => (entry.key === key ? { ...entry, subject } : entry)));
  // Until Extract is pressed a PDF can be taken out on its own. Once reading has started, the questions came from all
  // of them together, so the only way back is Start over.
  const extractionStarted = phase !== 'idle' || jobs.length > 0;
  const removeFile = (key) => setFiles((prev) => prev.filter((entry) => entry.key !== key));

  // A refresh (or closing the tab) throws away the PDFs and the review, so while there is any, the browser asks first.
  // Browsers show their own wording for this ("Changes you made may not be saved"); a page cannot set it.
  const hasWork = files.length > 0 || questions.length > 0 || jobs.length > 0;
  useEffect(() => {
    if (!hasWork) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = ''; // older browsers need this to show the question
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasWork]);

  // ---- resume on coming back ------------------------------------------------------
  useEffect(() => {
    if (freshVisit.current) {
      // A refresh in the middle of a review clears it; say so, so it does not look like the work vanished.
      if (reloaded.current && readSaved()) {
        Notification.warning('Started over', 'The page was refreshed, so Scan PDF started over. Add the PDFs again.');
      }
      writeSaved(null); // a new visit: the last review is not shown again
      keptFiles = [];
      return;
    }
    const saved = readSaved();
    if (saved && saved.jobs && saved.jobs.length) {
      savedReview.current = saved;
      if (saved.selection) {
        setSelection({ ...saved.selection, chapters: withSubjects(saved.selection.chapters, saved.selection.subject) });
      }
      if (saved.marks) setMarks(saved.marks);
      if (saved.title) setTitle(saved.title);
      if (saved.testKind) setTestKind(saved.testKind);
      if (saved.subjMarks) setSubjMarks(saved.subjMarks);
      setMarksDone(Boolean(saved.marksDone));
      if (saved.qMarks) setQMarks(saved.qMarks);
      if (saved.subjectChoice) setSubjectChoice(saved.subjectChoice);
      // The PDFs come back too (kept in memory while moving around the app), with the Generate answers tick, so
      // the form looks as it was left and Extract stays off until something on it changes.
      setFiles(filesToRestore.current);
      setGenerateAnswers(Boolean(saved.generateAnswers));
      setReadAfresh(Boolean(saved.readAfresh));
      if (saved.sig) setExtractedSig(saved.sig);
      setJobs(saved.jobs.map((j) => ({ id: j.id, filename: j.filename || '', subject: j.subject || '', data: null })));
      setPhase('extracting');
    }
  }, []);
  useEffect(() => { keptFiles = files; }, [files]);

  useEffect(() => {
    if (!jobs.length || (phase !== 'extracting' && phase !== 'ready')) return;
    writeSaved({
      jobs: jobs.map((j) => ({ id: j.id, filename: j.filename || (j.data && j.data.filename) || '', subject: j.subject })),
      selection, marks, subjMarks, marksDone, qMarks, title, testKind, keep, answers, pairs, modes, overrides, reviewed, subjectChoice,
      generateAnswers, readAfresh, sig: extractedSig, passageLinks, newPassages
    });
  }, [jobIdsKey, phase, selection, marks, subjMarks, marksDone, qMarks, title, testKind, keep, answers, pairs, modes, overrides, reviewed, subjectChoice,
    generateAnswers, readAfresh, extractedSig, passageLinks, newPassages]);

  // ---- extraction ----------------------------------------------------------
  const loadQuestions = useCallback((list, ids) => {
    const saved = savedReview.current && savedReview.current.jobs.map((j) => j.id).join(',') === ids ? savedReview.current : null;
    // A saved pairing (including "none", after the teacher split everything) wins over a fresh guess.
    const nextPairs = saved && saved.pairs ? saved.pairs : findPairsPerJob(list);
    const nextKeep = {};
    const nextAnswers = {};
    buildUnits(list, nextPairs).forEach((q) => {
      nextKeep[q.id] = isImportable(q, kind) ? (saved?.keep?.[q.id] ?? false) : false; // nothing is ticked for the teacher
      const base = saved?.answers?.[q.id] ?? parsePrintedAnswer(q);
      nextAnswers[q.id] = sanitizeAnswer(q, base ?? emptyAnswer(q));
    });
    setQuestions(list);
    setPairs(nextPairs);
    setModes((saved && saved.modes) || {});
    setOverrides((saved && saved.overrides) || {});
    setReviewed((saved && saved.reviewed) || {});
    setPassageLinks((saved && saved.passageLinks) || {});
    setNewPassages((saved && saved.newPassages) || {});
    setKeep(nextKeep);
    setAnswers(nextAnswers);
  }, [kind]);

  useEffect(() => {
    if (phase !== 'extracting' || !jobIdsKey) return undefined;
    let cancelled = false;
    const ids = jobIdsKey.split(',');
    (async () => {
      while (!cancelled) {
        try {
          const current = await Promise.all(ids.map((id) => getJob(id)));
          if (cancelled) return;
          setJobs((prev) => prev.map((j, i) => ({ ...j, data: current[i] })));
          const failed = current
            .map((data, i) => ({ data, name: jobsRef.current[i].filename || data.filename || 'A PDF' }))
            .filter((x) => x.data.status === 'failed');
          // Only when nothing could be read is the run over. A PDF that failed among others is left out, and the
          // questions of the rest are shown (the results say which one is missing).
          if (failed.length === current.length) {
            failRun(failed.map((x) => `${x.name}: ${x.data.error || 'could not be read'}`).join('. '));
            return;
          }
          if (current.every((data) => ['completed', 'partial', 'failed'].includes(data.status))) {
            const read = ids.map((id, i) => ({ id, i })).filter(({ i }) => current[i].status !== 'failed');
            const lists = await Promise.all(read.map(({ id }) => getQuestions(id)));
            if (cancelled) return;
            if (failed.length) {
              Notification.error(
                failed.length === 1 ? 'One PDF could not be read' : `${failed.length} PDFs could not be read`,
                `${failed.map((x) => `${x.name}: ${x.data.error || 'could not be read'}`).join('. ')}. The questions from the other PDFs are shown.`
              );
            }
            // Each question remembers its PDF and that PDF's subject, so pairs stay inside one PDF and the
            // paper can be split into one section per subject.
            const merged = lists.flatMap((list, n) => {
              const { id, i } = read[n];
              return list.map((q) => ({
                ...q,
                jobId: id,
                subjectValue: subjectFor(q.subject, jobsRef.current[i].subject, selectedSubjects, subjectLabel),
                fileName: jobsRef.current[i].filename || current[i].filename || ''
              }));
            });
            loadQuestions(merged, jobIdsKey);
            setPhase('ready');
            return;
          }
        } catch (err) {
          if (cancelled) return;
          failRun(err.message);
          return;
        }
        await sleep(POLL_INTERVAL_MS);
      }
    })();
    return () => { cancelled = true; };
  }, [phase, jobIdsKey, loadQuestions]);

  useEffect(() => {
    if (!busy) return undefined;
    setElapsed(0);
    const id = setInterval(() => setElapsed((prev) => prev + 1), 1000);
    return () => clearInterval(id);
  }, [busy]);

  // The chapters ticked for one subject. With a single subject that is simply every ticked chapter,
  // so a resumed review does not depend on the chapter list having finished loading.
  const chaptersOfSubject = (value) =>
    selectedSubjects.length === 1
      ? tickedNames(selection.chapters)
      : tickedNames(selection.chapters, value);

  // What the scan service is told about the PDF: its subject, every subject of the test, and the chapters the
  // institute has for each, so each question can be labelled with names the question bank already uses.
  const uploadContext = (subjectValue) => {
    const byLabel = {};
    selectedSubjects.forEach((v) => {
      const names = visibleChapters.filter((c) => c.subjectValue === v).map((c) => c.value);
      if (names.length) byLabel[subjectLabel(v)] = names;
    });
    // A PDF with every subject in it makes no claim about its subject: each question is read for its own.
    const subject = subjectValue === MIXED ? null : subjectLabel(subjectValue);
    return { subject, subjects: selectedSubjects.map(subjectLabel), chapters: byLabel };
  };

  const checks = useMemo(() => {
    const out = {};
    units.forEach((u) => {
      out[u.id] = checkQuestion(u, {
        taggedSubject: u.subjectValue ? subjectLabel(u.subjectValue) : '',
        chosenChapters: chaptersOfSubject(u.subjectValue)
      });
    });
    return out;
  }, [units, selection.chapters, selection.subject, chapters, subjects]);
  // Where the AI and the teacher disagree, the AI's choice is the default: "Keep my choice" (or picking a chapter)
  // takes a question back.
  // 1. Subject: a question the AI places in another subject of this test goes there. A subject the test does not
  //    have cannot take it, so it stays where the PDF put it.
  const subjectOfName = (name) =>
    selectedSubjects.find((v) => sameSubject(name, subjectLabel(v)) || sameSubject(name, v)) || null;
  const moved = useMemo(() => {
    const out = {};
    units.forEach((u) => {
      const check = checks[u.id];
      if (!check || check.status !== 'subject') return;
      const target = subjectOfName(check.suggestedSubject);
      if (!target || target === u.subjectValue) return;
      const choice = subjectChoice[u.id];
      if (choice === 'ai' || (!choice && !reviewed[u.id])) out[u.id] = target;
    });
    return out;
  }, [units, checks, subjectChoice, reviewed, selection.subject, subjects]);
  // The questions as they will be filed, moved questions under their new subject...
  const placed = useMemo(() => units.map((u) => (moved[u.id] ? { ...u, subjectValue: moved[u.id] } : u)), [units, moved]);
  const placedOf = (u) => placed.find((p) => p.id === u.id) || u;
  // ...and a moved question checked again, against the chapters of its new subject.
  const placedChecks = useMemo(() => {
    const out = { ...checks };
    placed.forEach((u) => {
      if (!moved[u.id]) return;
      out[u.id] = checkQuestion(u, { taggedSubject: subjectLabel(u.subjectValue), chosenChapters: chaptersOfSubject(u.subjectValue) });
    });
    return out;
  }, [placed, checks, moved, selection.chapters]);
  // 2. Chapter: the chapter the AI names, one of the subject's chapters in the pattern or, when none fitted, its own.
  const aiChapterOf = (u) => {
    const check = placedChecks[u.id];
    if (!check || check.status !== 'chapter' || !check.suggestedChapter) return null;
    if (check.offList) return check.suggestedChapter;
    const wanted = String(check.suggestedChapter).trim().toLowerCase();
    const match = chapters.find(
      (c) => (selectedSubjects.length === 1 || c.subjectValue === u.subjectValue) && String(c.value).trim().toLowerCase() === wanted
    );
    return match ? match.value : null;
  };
  const aiFiled = useMemo(() => {
    const out = {};
    placed.forEach((u) => {
      const ai = aiChapterOf(u);
      if (!ai || !isImportable(u, kind) || !keep[u.id]) return;
      const picked = overrides[u.id];
      const followsAi = picked
        ? String(picked).trim().toLowerCase() === String(ai).trim().toLowerCase()
        : !reviewed[u.id] || subjectChoice[u.id] === 'ai'; // the AI's subject brings the AI's chapter with it
      if (followsAi) out[u.id] = ai;
    });
    return out;
  }, [placed, placedChecks, overrides, reviewed, subjectChoice, keep, kind, chapters]);
  // The AI's chapters in use for one subject; they join the test's chapters, and Create Test offers to add the ones
  // the syllabus does not have.
  const aiChaptersOf = (value) => Array.from(new Set(
    placed.filter((u) => aiFiled[u.id] && (selectedSubjects.length === 1 || u.subjectValue === value)).map((u) => aiFiled[u.id])
  ));
  const chaptersWithAi = (value) => {
    const own = chaptersOfSubject(value);
    return [...own, ...aiChaptersOf(value).filter((n) => !own.some((o) => o.toLowerCase() === n.toLowerCase()))];
  };
  // The chapter each question will be filed under, worked out exactly as the paper is built.
  const filing = useMemo(() => {
    const out = {};
    placed.forEach((u) => {
      const q = composeQuestion(u, modes[u.id] || 'both');
      // Only this question's own AI chapter is a choice for it: another question's must not catch it by its topic.
      const names = aiFiled[u.id] ? [...chaptersOfSubject(u.subjectValue), aiFiled[u.id]] : chaptersOfSubject(u.subjectValue);
      out[u.id] = chapterFor({ topic: q.topic, chapter: u.chapter }, placedChecks[u.id], aiFiled[u.id] || overrides[u.id], names, true);
    });
    return out;
  }, [placed, placedChecks, overrides, aiFiled, modes, selection.chapters, selection.subject, chapters]);
  const toLookAt = units.filter((u) => isImportable(u, kind) && keep[u.id] && needsLook(checks[u.id]) && !reviewed[u.id]);
  const nothingChecked = units.length > 0 && units.every((u) => !checks[u.id] || checks[u.id].status === 'unchecked');
  // A whole PDF that looks like another subject is one notice, not a warning on every card.
  const subjectNotices = jobs.map((j) => {
    if (j.subject === MIXED) return null; // it claims no one subject, so it cannot be "tagged" the wrong one
    const own = units.filter((u) => u.jobId === j.id && isImportable(u, kind));
    const off = own.filter((u) => checks[u.id] && checks[u.id].status === 'subject' && !reviewed[u.id]);
    if (off.length < 3 || off.length / own.length < 0.5) return null;
    const counts = {};
    off.forEach((u) => { counts[checks[u.id].suggestedSubject] = (counts[checks[u.id].suggestedSubject] || 0) + 1; });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
    return { id: j.id, name: j.filename || (j.data && j.data.filename) || 'This PDF', tagged: subjectLabel(j.subject), suggested: top, ids: off.map((u) => u.id) };
  }).filter(Boolean);

  const validateForm = () => {
    if (!selection.pattern) return 'Pattern is required';
    if (!selection.class) return 'Class is required';
    if (selectedSubjects.length === 0) return 'Subject is required';
    if (selection.chapters.length === 0) return 'Select at least one chapter';
    const noChapters = selectedSubjects.filter((v) => chaptersOfSubject(v).length === 0);
    if (noChapters.length) return `Select at least one chapter for ${noChapters.map(subjectLabel).join(', ')}`;
    if (files.length === 0) return 'Choose the question paper PDF';
    if (files.length > MAX_PDFS) return `Choose at most ${MAX_PDFS} PDFs`;
    const untagged = files.find((entry) => !entry.subject);
    if (untagged) return `Choose the subject of ${untagged.file.name}`;
    const noPdf = files.some((entry) => entry.subject === MIXED)
      ? [] // a PDF holding every subject covers them all
      : selectedSubjects.filter((v) => !files.some((entry) => entry.subject === v));
    if (noPdf.length) return `Add a PDF for ${noPdf.map(subjectLabel).join(', ')}, or remove the subject`;
    return '';
  };

  const formSignature = (sel = selection) =>
    JSON.stringify([
      // Marks are set after reading, so changing them is no reason to read the paper again.
      sel, title, generateAnswers,
      files.map((entry) => [entry.file.name, entry.file.size, entry.subject])
    ]);

  const handleExtract = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const problem = validateForm();
    if (problem) {
      Notification.error('Cannot extract yet', problem);
      return;
    }
    setExtractedSig(formSignature());
    setQuestions([]);
    setJobs([]);
    savedReview.current = null;
    setPhase('uploading');
    try {
      // Uploaded together; the service reads two at a time and queues the rest.
      const created = await Promise.all(
        files.map((entry) => uploadPdf(entry.file, readAfresh, uploadContext(entry.subject), generateAnswers))
      );
      const dupe = created.findIndex((job, i) => created.findIndex((other) => other.id === job.id) !== i);
      if (dupe !== -1) {
        throw new Error(`${files[dupe].file.name} has the same pages as another PDF you added. Remove one of them.`);
      }
      setJobs(created.map((job, i) => ({ id: job.id, filename: files[i].file.name, subject: files[i].subject, data: job })));
      setPhase('extracting');
    } catch (err) {
      failRun(err.message);
    }
  };

  const startOver = () => {
    setReadAfresh(true); // a teacher starting over wants the paper read again, not the earlier result back
    writeSaved(null);
    savedReview.current = null;
    setJobs([]);
    setQuestions([]);
    setKeep({});
    setAnswers({});
    setPairs({});
    setModes({});
    setOverrides({});
    setReviewed({});
    setSubjectChoice({});
    setPassageLinks({});
    setNewPassages({});
    setFiles([]);
    setFileKey((k) => k + 1);
    setTitle('');
    setTestKind('');
    setSubjMarks({ correct: '', incorrect: '' });
    setMarksDone(false);
    setQMarks({});
    setExtractedSig('');
    setPhase('idle');
  };
  // Start over throws away every PDF and everything done with the questions, so it asks first.
  const confirmStartOver = () =>
    Modal.confirm({
      title: 'Start over?',
      content:
        'This removes the PDFs and every extracted question, with the ticks, answers and edits made to them. ' +
        'PDFs added after this are read again from the start, which uses AI credits.',
      okText: 'Start over',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      onOk: startOver
    });

  // ---- review ---------------------------------------------------------------
  const handleToggleKeep = (id) => setKeep((prev) => ({ ...prev, [id]: !prev[id] }));
  const handleKeepAll = (checked) => {
    const next = {};
    units.forEach((q) => { next[q.id] = checked && isImportable(q, kind); });
    setKeep(next);
  };
  const handleAnswer = (id, value) => setAnswers((prev) => ({ ...prev, [id]: value }));
  const handleSaveEdit = async (id, body) => {
    const saved = await patchQuestion(id, body);
    // The service does not know which PDF or subject a question came from, so carry that across the edit.
    setQuestions((prev) => prev.map((q) => (
      q.id === id ? { ...saved, jobId: q.jobId, subjectValue: q.subjectValue, fileName: q.fileName } : q
    )));
    // The Hindi twin of a joined card shares the English question's tick and answer, so edit only its text.
    if (Object.prototype.hasOwnProperty.call(pairs, id)) return;
    setAnswers((prev) => ({ ...prev, [id]: sanitizeAnswer(saved, prev[id] ?? emptyAnswer(saved)) }));
    setKeep((prev) => ({ ...prev, [id]: isImportable(saved, kind) ? (prev[id] ?? true) : false }));
  };
  const handleMode = (id, mode) => setModes((prev) => ({ ...prev, [id]: mode }));
  // Changing the kind of test after reading: a question the new kind cannot take is dropped; the rest keep their tick.
  // Nothing is ticked for the teacher, so a question the new kind can take starts unticked like every other.
  const handleKindChange = (value) => {
    setTestKind(value);
    setKeep((prev) => {
      const next = { ...prev };
      units.forEach((u) => { next[u.id] = isImportable(u, value) && Boolean(prev[u.id]); });
      return next;
    });
  };
  // Keep or drop every question under one passage at once.
  const handleToggleGroup = (passageId, checked) =>
    setKeep((prev) => {
      const next = { ...prev };
      units.forEach((u) => { if (u.passage_id === passageId) next[u.id] = checked && isImportable(u, kind); });
      return next;
    });
  // The passage is one text shared by its questions; the service fixes it for all of them from one question.
  // A passage the teacher wrote lives only here, so it is changed here.
  const handleEditPassage = async (passageId, text) => {
    if (String(passageId).startsWith(NEW_PASSAGE) || Object.prototype.hasOwnProperty.call(newPassages, passageId)) {
      setNewPassages((prev) => ({ ...prev, [passageId]: text }));
      return;
    }
    const first = questions.find((q) => q.passage_id === passageId);
    if (first) {
      await patchQuestion(first.id, { passage: text });
      setQuestions((prev) => prev.map((q) => (q.passage_id === passageId ? { ...q, passage: text } : q)));
    } else {
      setNewPassages((prev) => ({ ...prev, [passageId]: text })); // its questions were all moved here by the teacher
    }
  };
  // Put a question under a passage (one already there, or a new one with `text`), or take it out (passageId null).
  const handleSetPassage = (id, passageId, text) => {
    if (passageId && text !== undefined) setNewPassages((prev) => ({ ...prev, [passageId]: text }));
    setPassageLinks((prev) => ({ ...prev, [id]: passageId || null }));
  };
  // Remove a passage: its questions stay, each as a question of its own.
  const handleRemovePassage = (passageId) =>
    setPassageLinks((prev) => {
      const next = { ...prev };
      units.forEach((u) => { if (u.passage_id === passageId) next[u.id] = null; });
      return next;
    });
  const markReviewed = (ids) => setReviewed((prev) => ({ ...prev, ...Object.fromEntries(ids.map((id) => [id, true])) }));
  // Moving a question to another chapter is a decision too, so it counts as looked at.
  const handleOverride = (id, chapter) => {
    setOverrides((prev) => ({ ...prev, [id]: chapter }));
    markReviewed([id]);
  };
  // The AI's choice for a question, for its button: the subject of the test it belongs to (by name) when the AI
  // places it in another one, otherwise its chapter.
  const aiChoiceFor = (u) => {
    const check = checks[u.id];
    if (check && check.status === 'subject') {
      const target = subjectOfName(check.suggestedSubject);
      return target && target !== u.subjectValue ? subjectLabel(target) : null;
    }
    return aiChapterOf(placedOf(u));
  };
  // "Keep AI choice": the AI's subject or chapter, as the default already does, now decided. A chapter the test does
  // not have joins its chapters, and the next screen offers to add it to the syllabus.
  const handleKeepAiChoice = (id, choice) => {
    if (checks[id] && checks[id].status === 'subject') {
      setSubjectChoice((prev) => ({ ...prev, [id]: 'ai' }));
      markReviewed([id]);
      return;
    }
    handleOverride(id, choice);
  };
  // "Keep my choice": the question stays under the teacher's subject and chapters, even if the AI's was kept before.
  const handleKeepMine = (id) => {
    if (checks[id] && checks[id].status === 'subject') setSubjectChoice((prev) => ({ ...prev, [id]: 'mine' }));
    const ai = aiChapterOf(placedOf({ id }));
    if (ai && overrides[id] && String(overrides[id]).trim().toLowerCase() === String(ai).trim().toLowerCase()) {
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
    markReviewed([id]);
  };
  // Every question still to look at goes under the AI's choice, in one go.
  const handleKeepAllAi = () => {
    const picks = toLookAt.map((u) => [u, aiChoiceFor(u)]).filter(([, choice]) => choice);
    if (!picks.length) return;
    const bySubject = picks.filter(([u]) => checks[u.id].status === 'subject');
    const byChapter = picks.filter(([u]) => checks[u.id].status !== 'subject');
    if (bySubject.length) setSubjectChoice((prev) => ({ ...prev, ...Object.fromEntries(bySubject.map(([u]) => [u.id, 'ai'])) }));
    if (byChapter.length) setOverrides((prev) => ({ ...prev, ...Object.fromEntries(byChapter.map(([u, c]) => [u.id, c])) }));
    markReviewed(picks.map(([u]) => u.id));
  };
  // Undo a join that was wrong: the Hindi question becomes its own card, starting with the same tick and answer.
  const handleSplit = (id) => {
    const twinId = Object.keys(pairs).find((hindiId) => pairs[hindiId] === id);
    const twin = questions.find((q) => q.id === twinId);
    if (!twin) return;
    setPairs((prev) => {
      const next = { ...prev };
      delete next[twinId];
      return next;
    });
    setKeep((prev) => ({ ...prev, [twinId]: isImportable(twin, kind) ? (prev[id] ?? true) : false }));
    setAnswers((prev) => ({ ...prev, [twinId]: sanitizeAnswer(twin, prev[id] ?? emptyAnswer(twin)) }));
  };

  const status = reviewStatus(units, keep, answers, kind);
  // What the paper holds once read decides the test: a paper of one kind makes that kind of test, and a paper with
  // both makes a hybrid test where each question keeps its own type. A hybrid test takes one subject (as on Create
  // Test), so a mixed paper for several subjects stays objective. JEE and NEET stay objective whatever the paper holds.
  const paperKinds = useMemo(() => ({
    objective: units.filter((u) => isImportable(u, 'objective')).length,
    subjective: units.filter((u) => isImportable(u, 'subjective')).length
  }), [units]);
  const mixedPaper = !objectiveOnly && paperKinds.objective > 0 && paperKinds.subjective > 0;
  const severalSubjects = selectedSubjects.length > 1;
  const autoKind = objectiveOnly
    ? 'objective'
    : mixedPaper
      ? (severalSubjects ? 'objective' : 'hybrid')
      : paperKinds.objective === 0 && paperKinds.subjective > 0 ? 'subjective' : 'objective';
  // Once the kind is known, only questions it can take are shown; until then all of them are. A question read as
  // "other" is neither kind, so it stays, to be given a type with Edit.
  const otherKind = (u) => {
    if (kind === 'hybrid') return false;
    return kind === 'objective' ? u.question_type === 'subjective' : isImportable(u, 'objective');
  };
  const shownUnits = useMemo(
    () => (testKind ? units.filter((u) => !otherKind(u)) : units),
    [units, testKind]
  );
  // ---- marks, set once the paper is read ---------------------------------------
  // Each kind of question the test can take needs its marks: objective ones (unless the pattern fixes them) and written
  // ones. Negative marks are optional. A question's own marks, set in Edit, win over these. The card asks for every
  // kind the paper holds, ticked or not, since questions start unticked.
  const keptHere = (u) => isImportable(u, kind) && keep[u.id];
  const objectiveKept = kind !== 'subjective' && units.some((u) => isImportable(u, kind) && u.question_type !== 'subjective');
  const writtenKept = (kind === 'subjective' || kind === 'hybrid') && units.some((u) => isImportable(u, kind) && u.question_type === 'subjective');
  const fixedObjective = Boolean(rule && rule.byType);
  const objectiveMarksNeeded = objectiveKept && !fixedObjective;
  const marksProblem = objectiveMarksNeeded && !(Number(marks.correct) > 0)
    ? 'Enter the marks for each objective question'
    : objectiveMarksNeeded && !rule && Number(marks.incorrect) < 0
      ? 'Negative marks cannot be below zero'
      : writtenKept && !(Number(subjMarks.correct) > 0)
        ? 'Enter the marks for each subjective question'
        : writtenKept && Number(subjMarks.incorrect) < 0 ? 'Negative marks cannot be below zero' : '';
  const paperMarking = kind === 'subjective' ? undefined : buildMarkingByType(selection.pattern, marks, selection.class);
  // The marks one question goes into the test with, worked out the way the paper is built.
  const marksOfUnit = (u) => questionMarks(composeQuestion(u, modes[u.id] || 'both'), {
    markingByType: paperMarking, subjectiveMarks: subjMarks, testKind: kind, override: qMarks[u.id]
  });
  const handleQuestionMarks = (id, value) => setQMarks((prev) => {
    const next = { ...prev };
    if (value) next[id] = value;
    else delete next[id];
    return next;
  });
  const ready = status.ready && !marksProblem;

  // The marks card above the questions: the boxes for the kinds the test keeps, then, after Done, one line with Edit.
  // A pattern that fixes every mark of an objective-only paper has nothing to type, so it is one line from the start.
  const signed = (correct, incorrect) => `+${Number(correct) || 0} / −${Math.abs(Number(incorrect) || 0)}`;
  const objectiveText = () => {
    if (fixedObjective) {
      const types = Object.values(rule.byType);
      return types.every((m) => m.correct === types[0].correct && m.incorrect === types[0].incorrect)
        ? signed(types[0].correct, types[0].incorrect)
        : 'by question type';
    }
    return rule ? signed(marks.correct, shareOf(rule, marks.correct)) : signed(marks.correct, marks.incorrect);
  };
  const ownMarksCount = units.filter((u) => keptHere(u) && qMarks[u.id]).length;
  const marksBox = (label, value, onChange, required, testId, extra = {}) => (
    <div className="form-group">
      <label>{label}{required && <span className="mandatory-star">*</span>}</label>
      <Input type="number" min={0} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} {...extra} />
    </div>
  );
  const renderMarksCard = () => {
    if (phase !== 'ready' || (!objectiveKept && !writtenKept)) return null;
    const kinds = mixedPaper ? (
      <>
        We found <strong>{paperKinds.objective} objective</strong> and <strong>{paperKinds.subjective} subjective</strong>{' '}
        question{paperKinds.subjective === 1 ? '' : 's'} in this paper.{' '}
        {severalSubjects
          ? 'A test with both kinds takes one subject, and this one has several, so it is an objective test: the subjective questions are left out.'
          : 'It becomes a mixed test: every question goes in, each keeping its own type.'}
      </>
    ) : null;
    const nothingToType = !objectiveMarksNeeded && !writtenKept;
    if (nothingToType || (marksDone && !marksProblem)) {
      return (
        <div className="scan-kind-done" data-testid="marks-card">
          <span>
            Marks{mixedPaper && !severalSubjects ? ' · mixed test' : ''}
            {objectiveKept && <> · {writtenKept ? 'objective ' : ''}<strong>{objectiveText()}</strong>{fixedObjective ? ` (${rule.title})` : ''}</>}
            {writtenKept && <> · {objectiveKept ? 'subjective ' : ''}<strong>{signed(subjMarks.correct, subjMarks.incorrect)}</strong></>}
            {mixedPaper && severalSubjects && ' · subjective questions left out'}
            {ownMarksCount > 0 && ` · ${ownMarksCount} with their own marks`}
          </span>
          {!nothingToType && (
            <button type="button" className="link-btn" onClick={() => setMarksDone(false)} data-testid="marks-edit">Edit</button>
          )}
        </div>
      );
    }
    const both = objectiveKept && writtenKept;
    return (
      <div className={`scan-kind-choice ${marksProblem ? 'pending' : ''}`} data-testid="marks-card">
        {kinds && <p>{kinds}</p>}
        {objectiveKept && fixedObjective && (
          <p>Objective questions: <strong>{objectiveText()}</strong>, fixed by {rule.title}.{rule.partial ? ' Partial marking for multiple-correct questions comes from the marking scheme on the next screen.' : ''}</p>
        )}
        <div className="scan-subj-marks">
          {objectiveMarksNeeded && marksBox(both ? 'Objective: marks' : 'Marks per question', marks.correct,
            (v) => setMarks((prev) => ({ ...prev, correct: v })), true, 'objective-marks-correct', { placeholder: 'e.g. 4' })}
          {objectiveMarksNeeded && (rule
            ? marksBox(both ? 'Objective: negative' : 'Negative marks', shareOf(rule, marks.correct), () => {}, false, 'objective-marks-share', { disabled: true })
            : marksBox(both ? 'Objective: negative (optional)' : 'Negative marks (optional)', marks.incorrect,
              (v) => setMarks((prev) => ({ ...prev, incorrect: v })), false, 'objective-marks-incorrect', { placeholder: 'e.g. 1' }))}
          {writtenKept && marksBox(both ? 'Subjective: marks' : 'Marks per question', subjMarks.correct,
            (v) => setSubjMarks((prev) => ({ ...prev, correct: v })), true, 'subjective-marks-correct', { placeholder: 'e.g. 5' })}
          {writtenKept && marksBox(both ? 'Subjective: negative (optional)' : 'Negative marks (optional)', subjMarks.incorrect,
            (v) => setSubjMarks((prev) => ({ ...prev, incorrect: v })), false, 'subjective-marks-incorrect', { placeholder: 'e.g. 0' })}
          <Button type="primary" className="radius-100" disabled={Boolean(marksProblem)} onClick={() => setMarksDone(true)} data-testid="marks-done">
            Done
          </Button>
        </div>
        {rule && rule.negativeShare && objectiveMarksNeeded && (
          <p className="input-hint">{rule.title}: a wrong answer loses a quarter of the marks for a correct one.</p>
        )}
        {ownMarksCount > 0 && <p className="input-hint">{ownMarksCount} question{ownMarksCount === 1 ? ' has its' : 's have their'} own marks, set in Edit.</p>}
      </div>
    );
  };
  useEffect(() => {
    if (phase !== 'ready' || units.length === 0 || autoKind === testKind) return;
    handleKindChange(autoKind);
  }, [phase, autoKind, testKind, units.length]);

  const handleCreateTest = () => {
    if (marksProblem) {
      Notification.error('Cannot continue yet', marksProblem);
      const node = document.querySelector('[data-testid="marks-card"]');
      if (node && node.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!status.ready) {
      setFocusId(status.firstBlockingId);
      const node = status.firstBlockingId && document.getElementById(`scan-q-${status.firstBlockingId}`);
      if (node && node.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const noChapters = selectedSubjects.filter((v) => chaptersOfSubject(v).length === 0);
    const selectionProblem = !selection.pattern
      ? 'Choose the pattern above'
      : !selection.class
        ? 'Choose the class above'
        : selectedSubjects.length === 0
          ? 'Choose the subject above'
          : selection.chapters.length === 0
            ? 'Choose at least one chapter above'
            : noChapters.length
              ? `Choose at least one chapter for ${noChapters.map(subjectLabel).join(', ')} above`
              : '';
    if (selectionProblem) {
      Notification.error('Cannot continue yet', selectionProblem);
      const form = document.querySelector('.scan-pdf-tab .test-gen-form');
      if (form && form.scrollIntoView) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    // Every question under the subject it is filed under (the AI's, where it moved one).
    const items = placed
      .filter((q) => isImportable(q, kind) && keep[q.id])
      .map((q) => ({ q, answer: answers[q.id], mode: modes[q.id] || 'both' }));
    // A subjective paper has no marks of its own to send: they come from the marking scheme on the next screen.
    const subjective = kind === 'subjective';
    const markingByType = subjective ? undefined : buildMarkingByType(selection.pattern, marks, selection.class);
    const chaptersBySubject = {};
    selectedSubjects.forEach((v) => { chaptersBySubject[v] = chaptersWithAi(v); });
    // Every question goes under exactly the chapter its card shows, the AI's own included.
    const paper = buildGeneratedTest(
      items,
      subjective ? null : buildMarking(markingByType.single_mcq.correct, markingByType.single_mcq.incorrect),
      { markingByType, chaptersBySubject, subjectLabel, checks: placedChecks, overrides: { ...overrides, ...filing }, testKind: kind, subjectiveMarks: subjMarks, questionMarks: qMarks }
    );
    setProceeding(true);
    // The AI's own chapters are chapters of the test too; Create Test offers to add them to the syllabus.
    const aiNames = Array.from(new Set(Object.values(aiFiled)));
    const testChapters = Array.from(new Set([...tickedNames(selection.chapters), ...aiNames]));
    const handoff = buildSelection({
      ...selection,
      chapters: testChapters,
      syllabus: aiNames.length
        ? (testChapters.length > 1 ? 'Part' : 'Chapter') // more than the form's list, so never the full syllabus
        : syllabusFor(selection.chapters, visibleChapters.map(idOf)),
      title,
      testKind: kind
    }, items);
    startAiTestHandoff(handoff, paper, (subjectResolved) => {
      if (!subjectResolved) {
        Notification.error(
          'Subject not matched',
          `No subject named "${selectedSubjects.join(', ')}" exists for this institute. Pick one on the next screen.`
        );
      }
      // Lets the create form say "scanned" instead of "generated".
      setAiHandoff({ source: 'scanPdf' });
      history.push({
        pathname: '/create-test',
        state: { key: location?.state?.key, from: 'scanPdf' }
      });
    });
  };

  const renderSelect = (label, field, value, options, onChange, hint, mode, waiting) => {
    const isMultiple = mode === 'multiple';
    const disabled = busy || loading[field] || options.length === 0 || Boolean(waiting);
    return (
      <div className="form-group" style={{ flex: 1 }}>
        <label>{label}<span className="mandatory-star">*</span></label>
        <Select
          getPopupContainer={popupInside}
          mode={mode}
          value={isMultiple ? (Array.isArray(value) ? value : []) : (value || undefined)}
          onChange={onChange}
          disabled={disabled}
          placeholder={
            loading[field]
              ? `Loading ${field}...`
              : disabled && !busy
                ? hint
                : isMultiple ? `Select ${label.toLowerCase()}(s)` : `Select ${label.toLowerCase()}`
          }
          style={{ width: '100%' }}
          size="large"
          maxTagCount="responsive"
          notFoundContent={NOT_FOUND[field] || 'Nothing found'}
          suffixIcon={<img src={dropdownArrow} alt="dropdown" />}
          data-testid={`select-${field}`}
        >
          {options.map((option) => (
            <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
          ))}
        </Select>
      </div>
    );
  };

  const renderChapter = (chapter) => {
    const checked = selection.chapters.includes(idOf(chapter));
    return (
      <Checkbox
        key={chapter.key}
        className={`chapter-chip ${checked ? 'selected' : ''}`}
        checked={checked}
        disabled={busy}
        onChange={() => toggleChapter(chapter)}
      >
        <span>{chapter.label}</span>
        {notInSyllabus(chapter) && (
          <span className="scan-chapter-missing" title="Your institute has not set this chapter up yet. You can add it on the next screen.">
            not in your syllabus yet
          </span>
        )}
      </Checkbox>
    );
  };

  // With both classes chosen the chapters come in two sets; show each class on its own, as the Aulas AI form does.
  const renderChapterGroups = (list) => {
    const groups = groupChaptersByClass(list);
    if (groups.length <= 1) return <div className="chapters-grid">{list.map(renderChapter)}</div>;
    return groups.map((group) => (
      <div key={group.classKey} className="class-chapter-group" data-testid={`class-${group.classKey}`}>
        <div className="class-group-header">
          <div className="class-group-title-row">
            <span className="class-group-title">{group.className}</span>
            <span className="class-group-badge">{group.chapters.length} chapters</span>
          </div>
        </div>
        <div className="chapters-grid">{group.chapters.map(renderChapter)}</div>
      </div>
    ));
  };

  const failedFiles = jobs
    .filter((j) => j.data && j.data.status !== 'failed' && (j.data.failed_pages || []).length > 0)
    .map((j) => ({ name: j.filename || j.data.filename, pages: j.data.failed_pages }));
  // A PDF that could not be read at all, while the others were: its questions are simply not in the review.
  const unreadFiles = jobs
    .filter((j) => j.data && j.data.status === 'failed')
    .map((j) => ({ name: j.filename || j.data.filename || 'A PDF', error: j.data.error }));
  const showResults = phase === 'ready';
  const totalPages = jobs.reduce((sum, j) => sum + ((j.data && j.data.total_pages) || 0), 0);
  const pagesDone = jobs.reduce((sum, j) => sum + ((j.data && j.data.pages_done) || 0), 0);
  // Once every page is read, the service solves the unanswered questions; the bar then follows the answers.
  const answersTotal = jobs.reduce((sum, j) => sum + ((j.data && j.data.answers_total) || 0), 0);
  const answersDone = jobs.reduce((sum, j) => sum + ((j.data && j.data.answers_done) || 0), 0);
  const solving = answersTotal > 0 && jobs.some((j) => j.data && j.data.stage === 'answering');
  // One line per PDF in the pop-up: the files being uploaded, then the jobs reading them.
  const progressRows = phase === 'uploading'
    ? files.map((entry) => ({ key: entry.key, name: entry.file.name, data: null }))
    : jobs.map((j) => ({ key: j.id, name: j.filename || (j.data && j.data.filename) || 'PDF', data: j.data }));
  const formProblem = validateForm();
  const unchanged = extractedSig !== '' && extractedSig === formSignature();
  const subjectOptions = selectedSubjects.map((v) => ({ value: v, label: subjectLabel(v) }));

  return (
    <div className="test-gen-tab scan-pdf-tab" style={{ paddingTop: '80px' }}>
      <PageHeader title="Extract questions from a PDF" onBackPress={() => history.push('/test')} actions={[]} />

      {parserState === 'unconfigured' && (
        <div className="error-message scan-service-strip">
          <span>The scan service address is not set up for this build. Ask your administrator to set REACT_APP_PDF_PARSER_URL.</span>
        </div>
      )}
      {parserState === 'down' && (
        <div className="error-message scan-service-strip">
          <span>The scan service is unavailable right now.</span>
          <button type="button" className="retry-btn" onClick={runHealthCheck}>Retry</button>
        </div>
      )}

      <form onSubmit={handleExtract} className="test-gen-form">
        <div className="form-row">
          {renderSelect('Pattern', 'patterns', selection.pattern, patterns, handlePatternChange, 'No patterns available')}
          <div className="form-group" style={{ flex: 1 }}>
            <label>Title</label>
            <Input
              value={title}
              maxLength={100}
              disabled={busy}
              placeholder="Enter title"
              onChange={(e) => setTitle(e.target.value)}
              size="large"
              data-testid="title-input"
            />
          </div>
        </div>
        <div className="form-row">
          {renderSelect('Class', 'classes', selection.class, classes, handleClassChange, 'Select a pattern first')}
          {renderSelect(
            'Subject', 'subjects', selection.subject, subjects, handleSubjectChange,
            selection.pattern ? 'Select a class first' : 'Select a pattern first', 'multiple', !selection.class
          )}
        </div>
        {missingSubjects.length > 0 && (
          <p className="input-hint scan-hint-warn" data-testid="subject-missing">
            Your institute has no subject named {missingSubjects.map(subjectLabel).join(', ')} yet. You can add it on the
            next screen, and its chapters cannot be checked against your syllabus here.
          </p>
        )}
        {institute.status === 'unavailable' && (
          <p className="input-hint" data-testid="institute-unavailable">
            Your institute's subjects could not be read, so nothing is hidden. Some chapters may not exist in your institute.
          </p>
        )}

        <div className="form-group">
          <div className="section-label-row">
            <label>Chapters<span className="mandatory-star">*</span></label>
            {visibleChapters.length > 0 && !busy && (
              <div className="section-actions">
                <button type="button" className="link-btn" onClick={() => selectChapters(visibleChapters)}>Select all</button>
                <button
                  type="button"
                  className="link-btn"
                  disabled={selection.chapters.length === 0}
                  onClick={() => setChapterSelection([])}
                  data-testid="clear-chapters"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          {loading.chapters ? (
            <div className="chapters-empty"><span className="spinner dark"></span> Loading chapters...</div>
          ) : chapters.length === 0 ? (
            <div className="chapters-empty disabled" data-testid="chapters-disabled">
              <StopOutlined className="chapters-disabled-icon" />
              <span>
                {selection.pattern && selection.class && selectedSubjects.length > 0
                  ? 'No chapters available for the selected subject(s)'
                  : 'Select a pattern, class and subject to load chapters'}
              </span>
            </div>
          ) : selectedSubjects.length > 1 ? (
            // A mock test: chapters grouped under their subject, as on the Aulas AI form.
            selectedSubjects.map((value) => {
              const own = visibleChapters.filter((c) => c.subjectValue === value);
              return (
                <div key={value} className="scan-subject-group" data-testid={`chapters-${value}`}>
                  <div className="scan-subject-title-row">
                    <div className="scan-subject-title">{subjectLabel(value)}</div>
                    {own.length > 0 && !busy && (
                      <div className="section-actions">
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => selectChapters(own)}
                          data-testid={`select-all-${value}`}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="link-btn"
                          disabled={!own.some((c) => selection.chapters.includes(idOf(c)))}
                          onClick={() => clearChapters(own)}
                          data-testid={`clear-${value}`}
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                  {own.length === 0
                    ? <div className="chapters-empty">No chapters for this subject</div>
                    : renderChapterGroups(own)}
                </div>
              );
            })
          ) : (
            renderChapterGroups(visibleChapters)
          )}
          {missingChapterCount > 0 && (
            <p className="input-hint" data-testid="chapter-hint">
              {missingChapterCount} of {chapters.length} chapters are not in your institute's syllabus yet. You can add them on the next screen.
            </p>
          )}
          {syllabusUnreadable && (
            <p className="input-hint" data-testid="syllabus-unavailable">
              Your syllabus could not be read for every subject, so some chapters shown may not exist in your institute.
            </p>
          )}
        </div>

        <div className="form-group">
          <label>Question paper PDFs<span className="mandatory-star">*</span></label>
          <div className={`scan-file ${busy || files.length >= MAX_PDFS ? 'disabled' : ''}`}>
            <input
              ref={fileInput}
              key={fileKey}
              type="file"
              multiple
              accept="application/pdf,.pdf"
              style={{ display: 'none' }}
              disabled={busy || files.length >= MAX_PDFS}
              data-testid="pdf-input"
              onChange={(e) => addFiles(Array.from(e.target.files || []))}
            />
            <Button
              className="radius-100 scan-choose-btn"
              disabled={busy || files.length >= MAX_PDFS}
              onClick={() => fileInput.current && fileInput.current.click()}
              data-testid="choose-pdf"
            >
              {files.length >= MAX_PDFS ? (
                <span className="scan-choose-full" data-testid="choose-pdf-full">
                  <StopOutlined /> Add more PDFs
                </span>
              ) : files.length === 0 ? 'Choose PDFs' : 'Add more PDFs'}
            </Button>
            <span className="scan-file-name">
              {files.length >= MAX_PDFS ? `${MAX_PDFS} PDFs added, the most for one test` : 'Scanned or digital PDFs'}
            </span>
          </div>
          {files.length > 0 && (
            <div className="scan-file-list" data-testid="file-list">
              {files.map((entry) => (
                <div className="scan-file-item" key={entry.key} data-testid={`file-${entry.file.name}`}>
                  <span className="scan-file-item-name">
                    {entry.file.name} ({(entry.file.size / (1024 * 1024)).toFixed(1)} MB)
                  </span>
                  {selectedSubjects.length > 1 && (
                    <Select
                      getPopupContainer={popupInside}
                      value={entry.subject || undefined}
                      onChange={(value) => setFileSubject(entry.key, value)}
                      placeholder="Subject of this PDF"
                      disabled={busy}
                      style={{ width: 200 }}
                      suffixIcon={<img src={dropdownArrow} alt="dropdown" />}
                      data-testid={`file-subject-${entry.file.name}`}
                    >
                      <Select.Option key={MIXED} value={MIXED}>All subjects</Select.Option>
                      {subjectOptions.map((o) => (
                        <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
                      ))}
                    </Select>
                  )}
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => openPreview(entry)}
                    data-testid={`view-${entry.file.name}`}
                  >
                    View
                  </button>
                  {!extractionStarted && (
                    <button
                      type="button"
                      className="link-btn scan-remove-pdf"
                      disabled={busy}
                      onClick={() => removeFile(entry.key)}
                      data-testid={`remove-${entry.file.name}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              {/* Once reading has started, PDFs are not taken out one by one: Start over, above the questions, clears
                  them all and everything read from them. */}
            </div>
          )}
          <p className="input-hint">
            Up to {MAX_PDFS} PDFs, each up to {MAX_PDF_MB} MB. For a full mock test, add one PDF per subject, or one PDF with every subject in it.
            Reading takes about 3 seconds a page.
          </p>
        </div>

        <div className="form-group scan-answers-option">
          <Checkbox
            checked={generateAnswers}
            disabled={busy}
            onChange={(e) => setGenerateAnswers(e.target.checked)}
            data-testid="generate-answers"
          >
            Generate answers / solutions
          </Checkbox>
          <p className="input-hint scan-hint-cost">Uses more AI credits.</p>
        </div>

        <Button
          type="primary"
          htmlType="submit"
          className="generate-btn"
          disabled={busy || parserState === 'unconfigured' || parserState === 'down' || Boolean(formProblem) || unchanged}
          loading={busy}
          size="large"
        >
          {busy ? 'Reading...' : 'Extract Questions'}
        </Button>
      </form>

      <Modal
        visible={Boolean(preview)}
        title={preview ? preview.name : ''}
        footer={null}
        width={900}
        centered
        destroyOnClose
        onCancel={closePreview}
        className="scan-preview-modal"
      >
        {preview && <iframe title={`Preview of ${preview.name}`} src={preview.url} className="scan-preview-frame" data-testid="pdf-preview" />}
      </Modal>

      {(proceeding || handoffLoading) && (
        <div className="scan-fullpage-loader" data-testid="proceed-loader">
          <Spin size="large" />
          <p>Preparing your test...</p>
        </div>
      )}

      {busy && (
        <Modal
          visible
          centered
          footer={null}
          closable={false}
          maskClosable={false}
          keyboard={false}
          width={440}
          className="scan-modal"
        >
          <div className="scan-modal-content" data-testid="scan-progress">
            <Spin size="large" />
            <p className="elapsed-counter" data-testid="scan-elapsed">{clock(elapsed)}</p>
            <h3 className="scan-modal-title">Extracting Questions...</h3>
            <p className="scan-modal-text">
              {phase === 'uploading'
                ? `Uploading ${files.length} PDF${files.length === 1 ? '' : 's'}. Please keep this tab open.`
                : solving
                  ? `Solved ${answersDone} of ${answersTotal} questions. Please keep this tab open.`
                  : totalPages > 0
                    ? `Read ${pagesDone} of ${totalPages} pages. Please keep this tab open.`
                    : 'Starting. Please keep this tab open.'}
            </p>
            {solving && phase !== 'uploading' && (
              <Progress percent={Math.round((100 * answersDone) / answersTotal)} status="active" data-testid="answers-progress" />
            )}
            {!solving && totalPages > 0 && phase !== 'uploading' && (
              <Progress percent={Math.round((100 * pagesDone) / totalPages)} status="active" />
            )}
            {progressRows.length > 1 && (
              <div className="scan-modal-files">
                {progressRows.map((row) => (
                  <div className="scan-modal-file" key={row.key}>
                    <span className="scan-modal-file-name">{row.name}</span>
                    <span className="scan-modal-file-status">{jobStatusText(phase, row.data)}</span>
                  </div>
                ))}
              </div>
            )}
            {elapsed > 240 && (
              <p className="elapsed-note">Long papers take a few minutes. It is still working.</p>
            )}
          </div>
        </Modal>
      )}

      {showResults && (
        <div className="results-section">
          <div className="results-header">
            <h3>Extracted Questions</h3>
            <div className="scan-header-actions">
              <button type="button" className="link-btn" onClick={confirmStartOver} data-testid="start-over">Start over</button>
            </div>
          </div>
          <div className="test-meta">
            {jobs.length === 1 && <span>{jobs[0].filename || (jobs[0].data && jobs[0].data.filename)}</span>}
            {jobs.length > 1 && <span data-testid="pdf-count">{jobs.length} PDFs</span>}
            {totalPages ? <span>{totalPages} pages</span> : null}
            {Object.keys(pairs).length > 0 && (
              <span data-testid="joined-count">{Object.keys(pairs).length} joined from Hindi and English</span>
            )}
          </div>
          {phase === 'ready' && unreadFiles.length > 0 && (
            <div className="warning-message" data-testid="unread-pdfs">
              <p>
                {unreadFiles.map((f) => `${f.name}${f.error ? ` (${f.error})` : ''}`).join('; ')}{' '}
                could not be read, so {unreadFiles.length === 1 ? 'its' : 'their'} questions are missing. The questions
                from the other PDFs are below.
              </p>
              <button type="button" className="retry-btn" onClick={startOver}>Upload again</button>
            </div>
          )}
          {phase === 'ready' && failedFiles.length > 0 && (
            <div className="warning-message">
              <p>
                Some pages could not be read, so their questions are missing:{' '}
                {failedFiles.map((f) => `${f.name} (page${f.pages.length === 1 ? '' : 's'} ${f.pages.join(', ')})`).join('; ')}.
              </p>
              <button type="button" className="retry-btn" onClick={startOver}>Upload again</button>
            </div>
          )}
          {subjectNotices.map((notice) => (
            <div className="warning-message" key={notice.id} data-testid={`notice-${notice.id}`}>
              <p>
                Most questions in {notice.name} look like {notice.suggested}, but it is tagged {notice.tagged}.{' '}
                {subjectOfName(notice.suggested)
                  ? `They go under ${notice.suggested}, the AI's choice, unless you keep them as ${notice.tagged}.`
                  : 'Check you chose the right subject. You can still carry on.'}
              </p>
              <button type="button" className="retry-btn" onClick={() => markReviewed(notice.ids)}>Keep as {notice.tagged}</button>
            </div>
          ))}
          {toLookAt.length > 0 && (
            <div className="warning-message" data-testid="look-summary">
              <p>
                {toLookAt.length} question{toLookAt.length === 1 ? '' : 's'} may not be in the subject or chapter you chose.
                Unless you keep your choice, each goes under the AI&apos;s subject and chapter, so you can carry on as it is.
              </p>
              {toLookAt.some((u) => aiChoiceFor(u)) && (
                <button type="button" className="retry-btn" onClick={handleKeepAllAi} data-testid="keep-all-ai">
                  Keep AI choice for all
                </button>
              )}
            </div>
          )}
          {nothingChecked && (
            <p className="input-hint" data-testid="not-checked">
              Subject and chapter could not be checked for these questions, so check them yourself.
            </p>
          )}
          {questions.length === 0 ? (
            <div className="warning-message">
              <p>No questions were found in {jobs.length > 1 ? 'these PDFs' : 'this PDF'}. Try another file or a clearer scan.</p>
            </div>
          ) : (
            <ScanReview
              questions={shownUnits}
              objectiveOnly={objectiveOnly}
              kindChoice={renderMarksCard()}
              keep={keep}
              answers={answers}
              modes={modes}
              onMode={handleMode}
              onSplit={handleSplit}
              focusId={focusId}
              testKind={kind}
              onToggleGroup={handleToggleGroup}
              onEditPassage={handleEditPassage}
              passages={passageChoices(units)}
              onSetPassage={handleSetPassage}
              onRemovePassage={handleRemovePassage}
              subjectLabel={selectedSubjects.length > 1 ? subjectLabel : null}
              checks={checks}
              filing={filing}
              reviewed={reviewed}
              subjectName={subjectLabel}
              chapterChoices={(u) => {
                // The chapters of the subject it is filed under, and the AI's chapter for it.
                const at = placedOf(u);
                const own = chaptersOfSubject(at.subjectValue);
                const ai = aiChapterOf(at);
                return ai && !own.includes(ai) ? [...own, ai] : own;
              }}
              aiFiled={aiFiled}
              // Each question's marks once the paper's are set; a question's own marks are set in its Edit.
              marksOf={marksProblem ? null : marksOfUnit}
              ownMarks={qMarks}
              onQuestionMarks={handleQuestionMarks}
              movedTo={Object.fromEntries(Object.entries(moved).map(([id, value]) => [id, subjectLabel(value)]))}
              onOverride={handleOverride}
              aiChoiceFor={aiChoiceFor}
              onKeepAiChoice={handleKeepAiChoice}
              onReviewed={handleKeepMine}
              onToggleKeep={handleToggleKeep}
              onKeepAll={handleKeepAll}
              onAnswer={handleAnswer}
              onSaveEdit={handleSaveEdit}
            />
          )}
          {questions.length > 0 && (
            <div className="scan-footer" data-testid="scan-footer">
              {!ready && (
                <p className="scan-gate-hint" data-testid="gate-hint">
                  {status.ready && marksProblem
                    ? `${marksProblem} above to continue.`
                    : status.kept === 0
                      ? 'Keep at least one question to continue.'
                      : status.needAnswer > 0
                        ? `Pick the right answer for ${status.needAnswer} question${status.needAnswer === 1 ? '' : 's'} to continue.`
                        : `Fix ${status.needFix} question${status.needFix === 1 ? '' : 's'} to continue.`}
                </p>
              )}
              <Button
                htmlType="button"
                loading={handoffLoading}
                className={`purpleBtnWrap radius-100 purpleButtonHoverEffect scan-create-btn ${ready ? '' : 'not-ready'}`}
                style={{ minWidth: 150 }}
                onClick={handleCreateTest}
                data-testid="create-test"
              >
                Proceed
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const { loaders } = state.testAdmin;
  return { handoffLoading: !!loaders?.aiHandoffLoader };
};

const mapDispatchToProps = (dispatch) => ({
  startAiTestHandoff: (selection, generatedTest, callback) =>
    dispatch(startAiTestHandoff(selection, generatedTest, callback)),
  setAiHandoff: (values) => dispatch(setAiHandoff(values))
});

export default connect(mapStateToProps, mapDispatchToProps)(ScanPdfTab);
